import React, { useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/shared/ui/card';
import { Button } from '@/shared/ui/button';
import { Badge } from '@/shared/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import {
  Download,
  Loader2,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  ChevronRight,
  FileText,
  User,
  Calendar,
  Activity,
} from 'lucide-react';
import {
  AuditLog,
  getActionBadge,
  getResourceTypeBadge,
  formatCreatedAt,
  getStatusColor
} from '@/shared/types/auditLogTypes';
import { useInfiniteAccountAuditLogs } from '@/domains/accounts/services/account-audit-logs';
import EmptyView from '@/shared/components/EmptyView';
import AuditLogDetailsModal from '@/shared/components/AuditLogDetailsModal';
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from "@/shared/ui/table";
import { exportToCsv } from '@/shared/utils/csv-export-utils';
import SearchBar from '@/shared/components/SearchBar';

type SortField = 'createdAt' | 'action' | 'resourceType';
type SortDirection = 'asc' | 'desc';

export const AuditLogsTab = () => {
  const queryClient = useQueryClient();
  const {
    data: auditLogsInfiniteData,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    error,
  } = useInfiniteAccountAuditLogs();

  const auditLogs = useMemo(() => {
    const pages = auditLogsInfiniteData?.pages ?? [];
    const seen = new Set<number>();
    const merged: AuditLog[] = [];
    for (const page of pages) {
      for (const log of page.results ?? []) {
        if (!seen.has(log.id)) {
          seen.add(log.id);
          merged.push(log);
        }
      }
    }
    return merged;
  }, [auditLogsInfiniteData?.pages]);

  const totalCount = auditLogsInfiniteData?.pages[0]?.totalCount ?? 0;
  const [searchTerm, setSearchTerm] = React.useState('');
  const [actionFilter, setActionFilter] = React.useState<string>('all');
  const [resourceTypeFilter, setResourceTypeFilter] = React.useState<string>('all');

  // Sorting state
  const [sortField, setSortField] = React.useState<SortField>('createdAt');
  const [sortDirection, setSortDirection] = React.useState<SortDirection>('desc');

  // Modal state
  const [selectedAuditLog, setSelectedAuditLog] = React.useState<AuditLog | null>(null);
  const [isModalOpen, setIsModalOpen] = React.useState(false);

  // Filter audit logs based on current filters
  const filteredAuditLogs = React.useMemo(() => {
    if (!auditLogs || (auditLogs?.length ?? 0) === 0) return [];

    return auditLogs?.filter(log => {
      // Action filter
      if (actionFilter !== 'all' && log.action !== actionFilter) return false;

      // Resource type filter
      if (resourceTypeFilter !== 'all' && log.resourceType !== resourceTypeFilter) return false;

      // Search filter
      const searchLower = searchTerm.toLowerCase()
      if (searchLower &&
        !log.emailId?.toLowerCase().includes(searchLower) &&
        !log.resourceType?.toLowerCase().includes(searchLower) &&
        !log.serviceLineNumber?.toLowerCase().includes(searchLower) &&
        !log.action?.toLowerCase().includes(searchLower)) {
        return false;
      }

      return true;
    });
  }, [auditLogs, actionFilter, resourceTypeFilter, searchTerm]);

  const handleViewDetails = (auditLog: AuditLog) => {
    setSelectedAuditLog(auditLog);
    setIsModalOpen(true);
  };

  // Sort filtered audit logs
  const sortedAuditLogs = React.useMemo(() => {
    if (!filteredAuditLogs || (filteredAuditLogs?.length ?? 0) === 0) return [];

    return [...filteredAuditLogs]?.sort((a, b) => {
      let comparison = 0;

      switch (sortField) {
        case 'createdAt':
          comparison = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
          break;
        case 'action':
          comparison = a.action.localeCompare(b.action);
          break;
        case 'resourceType':
          comparison = a.resourceType.localeCompare(b.resourceType);
          break;
        default:
          comparison = 0;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [filteredAuditLogs, sortField, sortDirection]);

  // Handle column sorting
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc'); // Default to descending for new fields
    }
  };

  // Get sort icon for column
  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ArrowUpDown className="h-4 w-4" />;
    }
    return sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />;
  };

  // Handle export action
  const handleExportAction = () => {
    // If there is no data to export, show an alert
    if (!auditLogs || auditLogs.length === 0) {
      alert('No data to export');
      return;
    }

    // If the number of service lines is less than the total count, ask for confirmation
    if (auditLogs.length < totalCount) {
      if (confirm(`Only ${auditLogs.length} of ${totalCount} records will be exported. There are more records available on the next page(s). Please confirm to export?`)) {
        handleExport();
      }
      return;
    }

    // Export the data
    handleExport();
  }

  // Export the data
  const handleExport = () => {
    if (!auditLogs || (auditLogs?.length ?? 0) === 0) {
      alert('No data to export');
      return;
    }

    const csvData = auditLogs?.map(log => {
      const actionBadge = getActionBadge(log.action);
      const resourceTypeBadge = getResourceTypeBadge(log.resourceType);

      return {
        'Timestamp': formatCreatedAt(log.createdAt),
        'User Email/ID': log.emailId || log.userId,
        'User Role': log.role,
        'Action': actionBadge.label,
        'Resource Type': resourceTypeBadge.label,
        'Service Line Number': log.serviceLineNumber || '-',
        'Service Line Name': log.serviceLineName || '-',
        'Status Code': log.metadata?.response?.statusCode || '-',
        'Region Code': log.regionCode,
        'Base Subscription Data': log.baseSubscriptionData || '-',
        'Overage Data': log.overageData || '-',
        'Kit Numbers': log.kitNumbers || '-'
      };
    });

    exportToCsv(csvData, `account_audit_logs`);
  };

  // Get unique actions and resource types for filters
  const uniqueActions = React.useMemo(() => {
    const actions = new Set(auditLogs?.map(log => log.action));
    return Array.from(actions).sort((a, b) => a.localeCompare(b));
  }, [auditLogs]);

  const uniqueResourceTypes = React.useMemo(() => {
    const types = new Set(auditLogs?.map(log => log.resourceType));
    return Array.from(types).sort((a, b) => a.localeCompare(b));
  }, [auditLogs]);

  return (
    <div className="space-y-4">
      {/* Audit Logs Header */}
      <div className="p-4 flex items-center justify-between rounded-lg border border-border bg-muted">
        <div>
          <h1 className="text-3xl font-bold">Audit Logs</h1>
          <p className="text-lg">Account activity and API usage tracking</p>
        </div>
        {auditLogs?.length > 0 && (
          <Button onClick={handleExportAction} className="gap-2">
            <Download className="h-4 w-4" />
            Export
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center gap-2">
          <Loader2 className="h-8 w-8 animate-spin" />
          <p className="text-sm">Loading audit logs...</p>
        </div>
      ) : (auditLogs?.length > 0 && !error ? (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Activity className="h-4 w-4" />
                  Total Activities
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-blue-500">{auditLogs?.length}</div>
                <p className="text-xs mt-1">All recorded activities</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Unique Users
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-500">
                  {new Set(auditLogs?.map(log => log.userId)).size}
                </div>
                <p className="text-xs mt-1">Different users</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Resource Types
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-purple-500">{uniqueResourceTypes.length}</div>
                <p className="text-xs mt-1">Different resource types</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Recent Activity
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-orange-500">
                  {auditLogs?.filter(log => {
                    const logDate = new Date(log.createdAt);
                    const oneDayAgo = new Date();
                    oneDayAgo.setDate(oneDayAgo.getDate() - 1);
                    return logDate > oneDayAgo;
                  }).length}
                </div>
                <p className="text-xs mt-1">Last 24 hours</p>
              </CardContent>
            </Card>
          </div>

          {/* Filters */}
          <div className="flex items-center gap-4 justify-between">
            {/* Search */}
            <div className="relative w-full max-w-1/2">
              <SearchBar value={searchTerm} onChange={setSearchTerm} placeholder="Search audit logs..." />
            </div>

            {/* Filters */}
            <div className="flex items-center gap-2">
              <Select value={actionFilter} onValueChange={setActionFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Action" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Actions</SelectItem>
                  {uniqueActions.map(action => {
                    const badge = getActionBadge(action);
                    return (
                      <SelectItem key={action} value={action}>
                        <Badge className={badge.className}>{badge.label}</Badge>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>

              <Select value={resourceTypeFilter} onValueChange={setResourceTypeFilter}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Resource Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Resources</SelectItem>
                  {uniqueResourceTypes.map(type => {
                    const badge = getResourceTypeBadge(type);
                    return (
                      <SelectItem key={type} value={type}>
                        <Badge className={badge.className}>{badge.label}</Badge>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Audit Logs Table */}
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto rounded-lg">
                <Table className="w-full">
                  <TableHeader>
                    <TableRow className="bg-muted">
                      <TableHead className="text-left py-3 px-4 font-medium font-bold">
                        <Button
                          variant="ghost"
                          onClick={() => handleSort('createdAt')}
                          className="flex items-center gap-2 transition-colors"
                        >
                          Timestamp
                          {getSortIcon('createdAt')}
                        </Button>
                      </TableHead>
                      <TableHead className="text-left py-3 px-4 font-medium font-bold">User</TableHead>
                      <TableHead className="text-left py-3 px-4 font-medium font-bold">
                        <Button
                          variant="ghost"
                          onClick={() => handleSort('action')}
                          className="flex items-center gap-2 transition-colors"
                        >
                          Action
                          {getSortIcon('action')}
                        </Button>
                      </TableHead>
                      <TableHead className="text-left py-3 px-4 font-medium font-bold">
                        <Button
                          variant="ghost"
                          onClick={() => handleSort('resourceType')}
                          className="flex items-center gap-2 transition-colors"
                        >
                          Resource
                          {getSortIcon('resourceType')}
                        </Button>
                      </TableHead>
                      <TableHead className="text-left py-3 px-4 font-medium font-bold">Service Line Number</TableHead>
                      <TableHead className="text-left py-3 px-4 font-medium font-bold">Service Line Name</TableHead>
                      <TableHead className="text-left py-3 px-4 font-medium font-bold">Base Plan</TableHead>
                      <TableHead className="text-left py-3 px-4 font-medium font-bold">Overage Allowed</TableHead>
                      <TableHead className="text-left py-3 px-4 font-medium font-bold">Status</TableHead>
                      <TableHead className="text-right py-3 px-4 font-medium font-bold"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedAuditLogs?.map((log) => {
                      const actionBadge = getActionBadge(log.action);
                      const resourceTypeBadge = getResourceTypeBadge(log.resourceType);
                      const statusColor = getStatusColor(log.metadata?.response?.statusCode || null);

                      return (
                        <TableRow
                          key={log.id}
                          className="border-b hover:bg-muted/50 cursor-pointer"
                          onClick={() => handleViewDetails(log)}
                        >
                          <TableCell className="py-3 px-4 text-sm">
                            {formatCreatedAt(log.createdAt)}
                          </TableCell>
                          <TableCell className="py-3 px-4">
                            <div className="flex flex-col items-start gap-1">
                              <span className="text-sm font-medium">{log.emailId || log.userId}</span>
                            </div>
                          </TableCell>
                          <TableCell className="py-3 px-4">
                            <Badge className={actionBadge.className}>
                              {actionBadge.label}
                            </Badge>
                          </TableCell>
                          <TableCell className="py-3 px-4">
                            <Badge className={resourceTypeBadge.className}>
                              {resourceTypeBadge.label}
                            </Badge>
                          </TableCell>
                          <TableCell className="py-3 px-4">
                            <div className="flex flex-col items-start gap-1">
                              <span className="text-sm font-medium">
                                {log.serviceLineNumber || '-'}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="py-3 px-4">
                            <div className="flex flex-col items-start gap-1">
                              <span className="text-sm font-medium">{log.serviceLineName || '-'}</span>
                            </div>
                          </TableCell>
                          <TableCell className="py-3 px-4">
                            <div className="flex flex-col items-start gap-1">
                              <span className="text-sm font-medium">
                                {log.baseSubscriptionData || '-'}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="py-3 px-4">
                            <div className="flex flex-col items-start gap-1">
                              <span className="text-sm font-medium">
                                {log.overageData || '-'}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="py-3 px-4">
                            <div className="flex flex-col items-start gap-1">
                              <span className={`text-sm font-medium ${statusColor}`}>
                                {log.metadata?.response?.statusCode || '-'}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="text-right py-3 px-4">
                            <div className="flex justify-end">
                              <ChevronRight className="h-4 w-4" />
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                  <TableFooter>
                    <TableRow className="bg-muted">
                      <TableCell colSpan={10} className="py-3">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                          <div className="flex min-w-0 flex-col gap-0.5 sm:flex-row sm:items-center sm:gap-2">
                            <span className="text-md">
                              Loaded <span className="font-medium">{auditLogs.length}</span>
                              {' '}of <span className="font-bold">{totalCount}</span> logs
                            </span>
                            {sortedAuditLogs.length !== auditLogs.length && (
                              <span className="text-sm">
                                ({sortedAuditLogs.length} match current search query)
                              </span>
                            )}
                          </div>
                          {hasNextPage ? (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="shrink-0 gap-2 self-start sm:self-auto"
                              disabled={isFetchingNextPage}
                              onClick={() => void fetchNextPage()}
                            >
                              {isFetchingNextPage ? (
                                <>
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                  Loading…
                                </>
                              ) : (
                                'Load more'
                              )}
                            </Button>
                          ) : null}
                        </div>
                      </TableCell>
                    </TableRow>
                  </TableFooter>
                </Table>
              </div>
            </CardContent>
            <CardFooter className="p-4">
              <CardDescription>
                Showing {sortedAuditLogs?.length} of {auditLogs?.length} audit log entries.
              </CardDescription>
            </CardFooter>
          </Card>
        </>
      ) : (
        <EmptyView
          icon={FileText}
          title="No Audit Logs"
          description="This account doesn't have any audit log entries to display."
        />
      ))}

      {/* Audit Log Details Modal */}
      <AuditLogDetailsModal
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        auditLog={selectedAuditLog}
      />
    </div>
  );
}
